
import { useGuideMode, GuideType, GuideStep as GuideStepType } from "@/hooks/use-guide-mode";
import { GuideStep } from "./GuideStep";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HelpCircle, LightbulbIcon, Play, ChevronRight, Brain, AlertTriangle, Loader2, List, Sparkles, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";

// Helper for localStorage
const getStoredStepOverview = (scenarioId: string | null): boolean => {
  if (!scenarioId) return false;
  
  try {
    const stored = localStorage.getItem(`guide_step_overview_${scenarioId}`);
    return stored ? stored === 'true' : false;
  } catch (e) {
    return false;
  }
};

const setStoredStepOverview = (scenarioId: string | null, isVisible: boolean): void => {
  if (!scenarioId) return;
  
  try {
    localStorage.setItem(`guide_step_overview_${scenarioId}`, isVisible.toString());
  } catch (e) {
    console.error("Error storing step overview state in localStorage:", e);
  }
};

interface GuideModeProps {
  currentScenario: string | null;
  usedTools: {
    draw: boolean;
    incision: boolean;
    ruler: boolean;
    angle: boolean;
  };
  onExit: () => void;
  onToolSelect?: (tool: string) => void;
  className?: string;
  isExpanded?: boolean;
  onAnalyze?: () => void;
}

export const GuideMode = ({
  currentScenario,
  usedTools,
  onExit,
  onToolSelect,
  className,
  isExpanded,
  onAnalyze
}: GuideModeProps) => {
  const {
    activeGuide,
    getCurrentStep,
    getStepProgress,
    nextStep,
    previousStep,
    isLastStep,
    isFirstStep,
    isLoading,
    error
  } = useGuideMode(currentScenario);

  const [completedSteps, setCompletedSteps] = useState<Record<string, boolean>>({});
  const [showStepOverview, setShowStepOverview] = useState(getStoredStepOverview(currentScenario));
  const currentStep = getCurrentStep();
  const progress = getStepProgress();
  
  // Save step overview visibility state
  useEffect(() => {
    setStoredStepOverview(currentScenario, showStepOverview);
  }, [showStepOverview, currentScenario]);

  // Track completed steps in localStorage
  useEffect(() => {
    if (currentStep?.id) {
      try {
        const storedCompletedSteps = localStorage.getItem(`guide_completed_steps_${currentScenario}`);
        if (storedCompletedSteps) {
          setCompletedSteps(JSON.parse(storedCompletedSteps));
        }
      } catch (e) {
        console.error("Error retrieving completed steps from localStorage:", e);
      }
    }
  }, [currentStep, currentScenario]);

  useEffect(() => {
    if (currentStep?.checkCompletion && currentStep.id) {
      const isCompleted = currentStep.checkCompletion(usedTools);
      if (isCompleted !== completedSteps[currentStep.id]) {
        const updatedCompletedSteps = {
          ...completedSteps,
          [currentStep.id]: isCompleted
        };
        
        setCompletedSteps(updatedCompletedSteps);
        
        // Store in localStorage
        try {
          localStorage.setItem(`guide_completed_steps_${currentScenario}`, JSON.stringify(updatedCompletedSteps));
        } catch (e) {
          console.error("Error storing completed steps in localStorage:", e);
        }
      }
    } else if (currentStep?.tool && currentStep.id) {
      const toolName = currentStep.tool === 'scalpel' ? 'incision' : currentStep.tool;
      const isCompleted = usedTools[toolName as keyof typeof usedTools];
      
      if (isCompleted !== completedSteps[currentStep.id]) {
        const updatedCompletedSteps = {
          ...completedSteps,
          [currentStep.id]: isCompleted
        };
        
        setCompletedSteps(updatedCompletedSteps);
        
        // Store in localStorage
        try {
          localStorage.setItem(`guide_completed_steps_${currentScenario}`, JSON.stringify(updatedCompletedSteps));
        } catch (e) {
          console.error("Error storing completed steps in localStorage:", e);
        }
      }
    }
  }, [currentStep, usedTools, completedSteps, currentScenario]);

  const handleNext = () => {
    nextStep();
  };

  const handlePrevious = () => {
    previousStep();
  };

  const handleAnalyze = () => {
    if (onAnalyze) {
      onAnalyze();
    }
  };

  const isCurrentStepCompleted = currentStep ? !!completedSteps[currentStep.id] : false;

  const handleStepSelect = (index: number) => {
    for (let i = 0; i < progress.total; i++) {
      if (i < index) {
        nextStep();
      } else if (i > index) {
        previousStep();
      }
    }
    setShowStepOverview(false);
  };

  const toggleStepOverview = () => {
    setShowStepOverview(!showStepOverview);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
        <p className="text-indigo-700 text-sm">Loading guide...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="bg-red-50/60 backdrop-blur-sm border-red-200/60">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-700">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!activeGuide) {
    return (
      <div className="flex flex-col items-center justify-center text-center space-y-4 bg-white/30 backdrop-blur-sm rounded-lg p-6">
        <div className="w-12 h-12 bg-indigo-100/60 rounded-full flex items-center justify-center">
          <HelpCircle className="h-6 w-6 text-indigo-500" />
        </div>
        <h3 className="text-lg font-medium text-indigo-800">No Guide Available</h3>
        <p className="text-sm text-indigo-600">There is no guide available for this scenario.</p>
      </div>
    );
  }

  if (!currentStep) {
    return (
      <motion.div 
        className={cn(
          "flex flex-col items-center space-y-4",
          isExpanded ? "pt-6" : "pt-2"
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className={cn(
          "bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full flex items-center justify-center shadow-md",
          isExpanded ? "w-20 h-20" : "w-16 h-16"
        )}>
          <Sparkles className={cn(
            "text-white",
            isExpanded ? "h-10 w-10" : "h-8 w-8"
          )} />
        </div>
        <h2 className={cn(
          "font-semibold text-indigo-800",
          isExpanded ? "text-2xl" : "text-xl"
        )}>
          {activeGuide.title}
        </h2>
        <p className={cn(
          "text-center text-indigo-600 max-w-xs",
          isExpanded ? "text-base" : "text-sm"
        )}>
          {activeGuide.description}
        </p>
        <Button 
          variant="gradient-indigo" 
          size={isExpanded ? "lg" : "default"}
          className={cn(
            "gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1",
            isExpanded ? "mt-6 px-6 py-6 text-base" : "mt-4"
          )}
          onClick={handleNext}
        >
          <Play className={isExpanded ? "h-5 w-5" : "h-4 w-4"} />
          Start Guide
        </Button>
      </motion.div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleStepOverview}
          className="gap-1 text-indigo-700 border-indigo-200/60 bg-white/40 hover:bg-white/60"
        >
          <List className="h-4 w-4" />
          {showStepOverview ? "Hide Steps" : "View All Steps"}
        </Button>
        
        <div className="text-xs font-medium text-indigo-500">
          Step {progress.current} of {progress.total}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showStepOverview ? (
          <motion.div
            key="step-overview"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="border border-indigo-100/60 bg-white/40 backdrop-blur-sm shadow-sm">
              <CardContent className="p-3">
                <ScrollArea className={cn(
                  "pr-3",
                  isExpanded ? "h-[350px]" : "h-[250px]"
                )}>
                  <div className="space-y-2">
                    {activeGuide.steps.map((step, index) => {
                      const isCompleted = step.id ? completedSteps[step.id] : false;
                      const isCurrent = index === progress.current - 1;
                      
                      return (
                        <div 
                          key={step.id || index}
                          className={cn(
                            "p-3 rounded-md transition-all duration-200 cursor-pointer",
                            "border",
                            isCurrent 
                              ? "bg-indigo-50/60 border-indigo-200/60" 
                              : "hover:bg-indigo-50/30 border-transparent hover:border-indigo-100/60"
                          )}
                          onClick={() => handleStepSelect(index)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={cn(
                                "flex items-center justify-center w-5 h-5 rounded-full text-xs",
                                isCompleted 
                                  ? "bg-green-100/60 text-green-600" 
                                  : isCurrent 
                                    ? "bg-indigo-100/60 text-indigo-600 ring-1 ring-indigo-300/60" 
                                    : "bg-gray-100/60 text-gray-500"
                              )}>
                                {isCompleted ? <Check className="h-3 w-3" /> : index + 1}
                              </div>
                              <span className={cn(
                                "text-sm font-medium line-clamp-1",
                                isCurrent ? "text-indigo-800" : "text-indigo-600"
                              )}>
                                {step.title}
                              </span>
                            </div>
                            {isCurrent && (
                              <div className="flex-shrink-0">
                                <ChevronRight className="h-4 w-4 text-indigo-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="current-step"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <GuideStep
              step={currentStep}
              progress={progress}
              onNext={handleNext}
              onPrevious={handlePrevious}
              isFirstStep={isFirstStep()}
              isLastStep={isLastStep()}
              isStepCompleted={isCurrentStepCompleted}
              onToolSelect={onToolSelect}
              isExpanded={isExpanded}
              onAnalyze={handleAnalyze}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {isLastStep() && (
        <motion.div 
          className="pt-2 flex justify-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Button 
            variant="outline" 
            onClick={onExit}
            className="w-full text-indigo-700 bg-white/40 border-indigo-200/60 hover:bg-white/60 transition-all duration-300"
          >
            <Brain className="h-4 w-4 mr-2" />
            Switch to Analysis
          </Button>
        </motion.div>
      )}
    </div>
  );
};
